import {
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Switch,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MIN_PEER_GROUP_MEMBERS,
  type SupervisorPeerCandidate,
  type SupervisorPeerGroup,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTimeAdminApi } from './api';
import { CenterModal } from './components/CenterModal';
import { toErrorMessage } from './errors';

/**
 * CO-SUPERVISION — the supervisors who cover for each other.
 *
 * Michael, 2026-09-30: "Cody and Grayson have got to be able to see each others'
 * employees in WLX to approve for each other. They should be THE exception to the rule
 * where supervisors should only see the employees who work directly under them. They
 * technically co-supervise all employees under them."
 *
 * 🔥 WHY A SCREEN AND NOT TWO NAMES IN THE SOURCE. The shortest version of this is a
 * hard-coded pair, and that shape has cost real money here before: the commission
 * exclusion list was applied to the employee dropdown and not to the bulk generator, so
 * two salaried men came off the picker and kept being paid. A rule about who can see
 * and approve whose pay should be visible to, and changeable by, the person accountable
 * for it — not readable only by whoever next opens the repository. It also means the
 * NEXT pair of co-supervisors, and there will be one, is a five-second edit rather than
 * a deploy.
 *
 * WHAT IT GRANTS: visibility and approval of the other members' direct reports, and
 * nothing else. Not admin, not the supervisor picker, not posting. A peer is a
 * supervisor of more people; they are not a different kind of user.
 *
 * SYMMETRIC BY CONSTRUCTION — a group is a set, so "A sees B but not the reverse"
 * cannot be expressed. A one-way version of this is how a stand-in arrangement quietly
 * becomes a privilege escalation nobody can see the direction of.
 */

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  intro: { color: tokens.colorNeutralForeground3, maxWidth: '70ch' },
  toolbar: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  groups: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  group: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    flexWrap: 'wrap',
  },
  groupBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  members: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  picker: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '44vh',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
  },
  candidate: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  state: { padding: tokens.spacingVerticalXXL, textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

/** The group being created or edited. `id` null = a new one. */
interface Draft {
  id: string | null;
  name: string;
  active: boolean;
  employeeIds: Set<string>;
}

export function CoSupervisionTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [groups, setGroups] = useState<readonly SupervisorPeerGroup[] | null>(null);
  const [candidates, setCandidates] = useState<readonly SupervisorPeerCandidate[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const [list, people] = await Promise.all([
      api.listSupervisorPeerGroups(),
      api.listSupervisorPeerCandidates(),
    ]);
    setGroups(list);
    setCandidates(people);
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [list, people] = await Promise.all([
          api.listSupervisorPeerGroups(),
          api.listSupervisorPeerCandidates(),
        ]);
        if (!cancelled) {
          setGroups(list);
          setCandidates(people);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadError(toErrorMessage(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const candidateById = useMemo(
    () => new Map(candidates.map((c) => [c.employeeId, c])),
    [candidates]
  );

  const save = useCallback(async (): Promise<void> => {
    if (draft === null) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      const input = {
        name: draft.name.trim(),
        active: draft.active,
        employeeIds: [...draft.employeeIds],
      };
      if (draft.id === null) {
        await api.createSupervisorPeerGroup(input);
      } else {
        await api.updateSupervisorPeerGroup(draft.id, input);
      }
      await reload();
      setDraft(null);
    } catch (err: unknown) {
      setActionError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, draft, reload]);

  const remove = useCallback(
    async (id: string): Promise<void> => {
      setActionError(null);
      try {
        await api.deleteSupervisorPeerGroup(id);
        await reload();
      } catch (err: unknown) {
        setActionError(toErrorMessage(err));
      } finally {
        setConfirmDeleteId(null);
      }
    },
    [api, reload]
  );

  if (loadError !== null) {
    return (
      <div className={styles.state} role="alert">
        <Text>{loadError}</Text>
      </div>
    );
  }
  if (groups === null) {
    return (
      <div className={styles.state} role="status">
        <Spinner size="tiny" label="Loading co-supervision…" />
      </div>
    );
  }

  const nameTrimmed = draft?.name.trim() ?? '';
  const enoughMembers = (draft?.employeeIds.size ?? 0) >= MIN_PEER_GROUP_MEMBERS;

  return (
    <div className={styles.page}>
      <Title3>Co-supervision</Title3>
      <Text className={styles.intro}>
        Supervisors in the same group see and approve each other&apos;s direct reports,
        which is the one exception to a supervisor only seeing their own team. It grants
        nothing else — no admin access, no posting. Everyone in a group can see everyone
        else&apos;s people, in both directions.
      </Text>

      <div className={styles.toolbar}>
        <Button
          appearance="primary"
          onClick={() => {
            setDraft({ id: null, name: '', active: true, employeeIds: new Set() });
            setActionError(null);
          }}
        >
          New group
        </Button>
      </div>

      {actionError !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{actionError}</MessageBarBody>
        </MessageBar>
      )}

      {groups.length === 0 ? (
        <Text className={styles.muted}>
          No co-supervision groups. Every supervisor sees only their own direct reports.
        </Text>
      ) : (
        <div className={styles.groups}>
          {groups.map((g) => (
            <div key={g.id} className={styles.group}>
              <div className={styles.groupBody}>
                <Text weight="semibold">
                  {g.name}
                  {!g.active && ' — off'}
                </Text>
                <div className={styles.members}>
                  {g.members.map((m) => (
                    <Badge
                      key={m.employeeId}
                      appearance="tint"
                      // A member who supervises NOBODY contributes nothing to the
                      // group, which looks like it worked and changes not one row.
                      // Said on the badge rather than left to be discovered.
                      color={m.directReports === 0 ? 'danger' : 'brand'}
                    >
                      {`${m.displayName} · ${String(m.directReports)}`}
                    </Badge>
                  ))}
                </div>
                <Text className={styles.muted}>
                  {`${String(g.members.reduce((n, m) => n + m.directReports, 0))} employees visible to each member`}
                </Text>
              </div>
              <div className={styles.actions}>
                <Button
                  onClick={() => {
                    setDraft({
                      id: g.id,
                      name: g.name,
                      active: g.active,
                      employeeIds: new Set(g.members.map((m) => m.employeeId)),
                    });
                    setActionError(null);
                  }}
                >
                  Edit
                </Button>
                {confirmDeleteId === g.id ? (
                  <Button appearance="secondary" onClick={() => void remove(g.id)}>
                    Confirm delete
                  </Button>
                ) : (
                  <Button
                    appearance="secondary"
                    onClick={() => {
                      setConfirmDeleteId(g.id);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CenterModal
        open={draft !== null}
        onClose={() => {
          setDraft(null);
        }}
        title={draft?.id === null ? 'New co-supervision group' : 'Edit co-supervision group'}
      >
        {draft !== null ? (
          <div className={styles.form}>
            <Field label="Name">
              <Input
                value={draft.name}
                onChange={(_, d) => {
                  setDraft({ ...draft, name: d.value });
                }}
                placeholder="Production floor — Cody &amp; Grayson"
              />
            </Field>

            <Switch
              checked={draft.active}
              label={draft.active ? 'On' : 'Off — grants nothing while off'}
              onChange={(_, d) => {
                setDraft({ ...draft, active: d.checked });
              }}
            />

            <Field
              label={`Supervisors (${String(draft.employeeIds.size)} selected)`}
              hint="Only people somebody actually reports to are listed — the number beside each name is how many direct reports they have right now."
            >
              <div className={styles.picker}>
                {candidates.map((c) => (
                  <div key={c.employeeId} className={styles.candidate}>
                    <Checkbox
                      checked={draft.employeeIds.has(c.employeeId)}
                      label={c.displayName}
                      onChange={() => {
                        const next = new Set(draft.employeeIds);
                        if (next.has(c.employeeId)) {
                          next.delete(c.employeeId);
                        } else {
                          next.add(c.employeeId);
                        }
                        setDraft({ ...draft, employeeIds: next });
                      }}
                    />
                    <Text className={styles.muted}>{`${String(c.directReports)} reports`}</Text>
                  </div>
                ))}
                {candidates.length === 0 && (
                  <Text className={styles.muted}>
                    Nobody on the roster has direct reports, so there is no one to group.
                  </Text>
                )}
              </div>
            </Field>

            {/*
              * Stated BEFORE the save is attempted rather than surfaced as a server
              * error afterwards. The server refuses it either way — that is the rule
              * that counts — but an admin setting this up should be told what a group
              * needs while they are still choosing, not after a round trip.
              */}
            {!enoughMembers && (
              <Text className={styles.muted}>
                {`Pick at least ${String(MIN_PEER_GROUP_MEMBERS)} supervisors — a group of one widens nobody's view.`}
              </Text>
            )}

            {/*
              * The one sentence that says what saving this will DO, in people rather
              * than in rows. Whoever is about to press the button is deciding who can
              * see whose pay, and the count is the only honest measure of that.
              */}
            {enoughMembers && (
              <Text className={styles.muted}>
                {(() => {
                  const total = [...draft.employeeIds].reduce(
                    (n, id) => n + (candidateById.get(id)?.directReports ?? 0),
                    0
                  );
                  return `Each of these ${String(draft.employeeIds.size)} supervisors will see and approve all ${String(total)} of their combined direct reports.`;
                })()}
              </Text>
            )}

            <div className={styles.actions}>
              <Button
                appearance="primary"
                disabled={saving || nameTrimmed === '' || !enoughMembers}
                onClick={() => void save()}
              >
                {saving ? <Spinner size="tiny" /> : 'Save'}
              </Button>
              <Button
                onClick={() => {
                  setDraft(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </CenterModal>
    </div>
  );
}
